import axios, { AxiosResponse } from "axios";
import { GetCookie } from "../functions/cookies";
// Removed: import { SetTextData } from "../providers/MessageProvider";

type HttpMethod = "get" | "post" | "patch" | "delete" | "GET" | "POST" | "PATCH" | "DELETE" | "put" | "PUT";

/**
 * Props for the new Promise-based HandleAxios function.
 * We remove onComplete, onError, and setTextData.
 */
type PromiseProps = {
  method?: HttpMethod;
  url: string;
  data?: any | null;
  contentType?: string;
  responseType?: string;
  additional_headers?: { [key: string]: string };
  external_api?: null | { api_key: string; url: string };
  timeout?: number;
};

const extractErrorMessage = (data: unknown): string | null => {
  if (typeof data === "string") {
    const trimmed = data.trim();
    return trimmed || null;
  }

  if (!data || typeof data !== "object") {
    return null;
  }

  const payload = data as Record<string, unknown>;
  const directKeys = ["error", "text", "message", "detail"];
  for (const key of directKeys) {
    const value = payload[key];
    if (typeof value === "string" && value.trim() !== "") {
      return value.trim();
    }
  }

  const parts: string[] = [];
  Object.entries(payload).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      const joined = value.map((item) => String(item)).join(", ").trim();
      if (joined) {
        parts.push(`${key}: ${joined}`);
      }
      return;
    }
    if (value === null || value === undefined) {
      return;
    }
    const text = String(value).trim();
    if (text) {
      parts.push(`${key}: ${text}`);
    }
  });

  return parts.length > 0 ? parts.join(" | ") : null;
};

const parseBlobErrorMessage = async (blob: Blob): Promise<string | null> => {
  try {
    const text = await blob.text();
    if (!text || text.trim() === "") {
      return null;
    }
    try {
      const parsed = JSON.parse(text);
      return extractErrorMessage(parsed) || text.trim();
    } catch {
      return text.trim();
    }
  } catch {
    return null;
  }
};

/**
 * A refactored version of HandleAxios that returns a Promise.
 * This allows you to use .then(), .catch(), and .finally() for async handling.
 *
 * @resolves {AxiosResponse} The full Axios response object on success.
 * @rejects {string} A processed, user-friendly error message on failure.
 *
 * @example
 * HandleAxiosPromise({
 * url: "/api/my-data",
 * method: "get"
 * })
 * .then(response => {
 * console.log("Success:", response.data);
 * setTextData({ text: "Data fetched!", variant: "success" });
 * })
 * .catch(errorMessage => {
 * console.error("Error:", errorMessage);
 * setTextData({ text: errorMessage, variant: "danger" });
 * })
 * .finally(() => {
 * setLoading(false);
 * });
 */
export const HandleAxiosPromise = ({ method = "post", url, data = null, contentType = "application/json", responseType = "json", additional_headers = {}, external_api = null, timeout = 120000 }: PromiseProps): Promise<AxiosResponse<any>> => {
  // Return a new Promise. The executor function is async to allow 'await' inside.
  return new Promise(async (resolve, reject) => {
    try {
      // --- 1. Build Headers ---
      let headers: { [key: string]: string } = {
        "Content-Type": contentType,
        "X-CSRFToken": GetCookie("csrfmiddlewaretoken") || "",
        ...additional_headers,
      };
      // --- 2. Handle External API Logic ---
      let full_url = url;
      if (external_api) {
        try {
          if (!external_api.api_key || !external_api.url) {
            throw new Error("Failed to get external API key");
          }
          if (!full_url.includes(external_api.url)) {
            full_url = `${external_api.url}${url}`;
          }
          headers["X-Api-Key"] = external_api.api_key;
        } catch (error) {
          console.error("Failed to get external API key");
          // If setup fails, reject the promise
          reject("Failed to get external API key");
          return;
        }
      }

      // --- 3. Configure Axios Request ---
      const config: any = {
        url: full_url,
        method,
        headers,
        responseType: responseType as any,
        timeout: timeout,
      };

      // For GET, use "params" instead of "data"
      if (method === "get" || method === "GET") {
        config.params = data;
      } else {
        config.data = data;
      }

      // --- 4. Make the Request ---
      const response = await axios.request(config);
      // --- 5. Success: Resolve the Promise ---
      // We resolve with the full response object, just like onComplete did.
      resolve(response);
    } catch (error: any) {
      // --- 6. Error: Process and Reject the Promise ---
      console.error("HandleAxios Error:", error);
      // --- All the original, detailed error processing logic ---
      let errorMessage = "An unexpected error occurred.";

      if (error.code === "ECONNABORTED") {
        errorMessage = `Request timed out after ${timeout / 1000} seconds. Please check your network connection or try again later.`;
        console.error(`Request timed out: ${error.message}`);
      } else if (error.response) {
        const { status, statusText, data, headers } = error.response;
        console.error(`Server responded with status ${status}:`, data);
        const headerText = headers?.["x-text"] || headers?.["X-text"] || headers?.["x-error-message"] || headers?.["X-Error-Message"];
        if (typeof headerText === "string" && headerText.trim() !== "") {
          reject(headerText.trim());
          return;
        }

        if (status === 429) {
          const extracted = extractErrorMessage(data);
          if (extracted) {
            errorMessage = extracted;
          } else {
            errorMessage = "Too many requests. Please slow down and try again later.";
          }
        }
        else if (data instanceof Blob) {
          const parsedBlobMessage = await parseBlobErrorMessage(data);
          if (parsedBlobMessage) {
            errorMessage = parsedBlobMessage;
          } else {
            errorMessage = `Server error (${status} ${statusText || ""}).`;
          }
        }
        else if (status === 404 ) {
          const extracted = extractErrorMessage(data);
          errorMessage = extracted || "The requested resource was not found.";
        }
        //turn a serializer error into a readable string
        else if (status === 400 && data) {
          const extracted = extractErrorMessage(data);
          errorMessage = extracted || "Bad request.";
        } else if (typeof data === "object" && data !== null) {
          const extracted = extractErrorMessage(data);
          errorMessage = extracted || `Server error (${status} ${statusText || ""}).`;
        } else {
          errorMessage = `Server error (${status} ${statusText || ""}).`;
        }
      } else if (error.request) {
        errorMessage = "No response received from server. Check network connection.";
        console.error("Network Error/No Response:", error.request);
      } else {
        errorMessage = `Request setup error: ${error.message}`;
        console.error("Request Setup Error:", error.message);
      }

      // Instead of calling onError or setTextData, we reject the promise
      // with the processed error message.
      reject(errorMessage);
    }
  });
};
